import { create } from 'zustand';

/**
 * Per-session "must view the Welcome page first" gate.
 *
 * Every login lands on /welcome and re-arms this gate (`requireWelcome`). Until
 * the user scrolls the whole welcome page (`acknowledge`, fired when the bottom
 * sentinel comes into view), non-admins are bounced back to /welcome from any
 * other route - so nobody skips it, not even by deep-linking back to the page
 * they were on when they got logged out. ADMIN is exempt from the bounce, as is
 * the single-screen marketing-only role (both handled in AppShell).
 *
 * Backed by localStorage (2026-10-31; was sessionStorage): scroll once per
 * login, not once per refresh - and not once per tab either. sessionStorage is
 * per tab, so every link opened in a new tab / window (a converted lead, a
 * notification) landed on /welcome instead of the page. A fresh login still
 * re-arms it, and other open tabs follow along through the storage event.
 */
const KEY = 'welcome:ack';
const read = (): boolean => {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
};

interface WelcomeGateState {
  acknowledged: boolean;
  /** Mark the welcome page as seen (scrolled to the end). */
  acknowledge: () => void;
  /** Re-arm the gate - call on every login so welcome is shown again. */
  requireWelcome: () => void;
}

export const useWelcomeGate = create<WelcomeGateState>((set) => ({
  acknowledged: read(),
  acknowledge: () => {
    try {
      localStorage.setItem(KEY, '1');
    } catch {
      /* private mode / blocked storage - gate just re-arms next render, fine */
    }
    set({ acknowledged: true });
  },
  requireWelcome: () => {
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
    set({ acknowledged: false });
  },
}));

// Another tab acknowledged (or a login there re-armed it): follow along.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === KEY || e.key === null) useWelcomeGate.setState({ acknowledged: read() });
  });
}
